import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { RefreshCw, Calendar as CalendarIcon, Clock, MapPin, Users, Plus, Music, Share2, Copy, Check, Import, FileOutput } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_URL, fetchWithAuth } from '../services/ApiService';
import EventModal from '../components/EventModal';
import DeleteEventDialog from '../components/DeleteEventDialog';
import EventCard from '../components/EventCard';
import { formatDateTime, formatTime, formatDate, getDateOrder } from '../utils/dateFormatter';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const EventsPage = () => {
  const { currentUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [activeTab, setActiveTab] = useState(isAuthenticated ? 'my' : 'all'); // 'my', 'band', 'all'
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [showRSVPDialog, setShowRSVPDialog] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState('no-response');
  const [rsvpReason, setRsvpReason] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [filters, setFilters] = useState({
    band: '',
    type: ''
  });
  
  // Configure moment based on system locale
  useEffect(() => {
    // Set week start based on locale (Monday for most of world, Sunday for US)
    const dateOrder = getDateOrder();
    moment.locale('en', {
      week: {
        dow: dateOrder === 'MDY' ? 0 : 1, // Sunday for US format, Monday for others
      }
    });
  }, []);

  // Fetch user's bands
  const fetchBands = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const data = await fetchWithAuth(`${API_URL}/bands`);
      if (data.success) {
        setBands(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching bands:', error);
    }
  }, [isAuthenticated]);

  // Fetch events from API
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query string
      const params = new URLSearchParams();
      if (filters.band) params.append('band', filters.band);
      if (filters.type) params.append('type', filters.type);
      
      console.log('Fetching events with params:', params.toString());
      
      // Use fetchWithAuth which handles authentication properly
      const data = await fetchWithAuth(`${API_URL}/events?${params}`);
      console.log('Fetched events:', data);
      
      if (data.success) {
        // Store raw events
        setEvents(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchEvents();
    fetchBands();
  }, [fetchEvents, fetchBands]);

  // Filter events based on active tab
  const getFilteredEvents = () => {
    let filtered = [...events];
    
    // Filter by tab
    if (activeTab === 'my' && currentUser) {
      filtered = filtered.filter(event => 
        event.owner === currentUser._id || 
        event.owner?._id === currentUser._id ||
        event.attendees?.some(a => 
          a.user === currentUser._id || 
          a.user?._id === currentUser._id
        )
      );
    } else if (activeTab === 'band') {
      // Show events from user's bands
      const userBandIds = bands.map(b => b._id);
      filtered = filtered.filter(event => 
        userBandIds.includes(event.band?._id || event.band)
      );
    }
    // 'all' shows everything (public events when not authenticated)
    
    return filtered.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  };

  // Get the next upcoming event
  const getNextEvent = () => {
    const now = new Date();
    const filtered = getFilteredEvents();
    return filtered.find(event => new Date(event.startTime) > now);
  };

  // Get event type styling
  const getEventTypeStyle = (type) => {
    const styles = {
      rehearsal: 'bg-green-100 text-green-800 border-green-200',
      concert: 'bg-orange-100 text-orange-800 border-orange-200',
      recording: 'bg-purple-100 text-purple-800 border-purple-200',
      meeting: 'bg-blue-100 text-blue-800 border-blue-200',
      audition: 'bg-red-100 text-red-800 border-red-200',
      default: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return styles[type] || styles.default;
  };

  // Handle event selection
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
    // Set current user's RSVP status if they have one
    if (currentUser && event.attendees) {
      const userAttendee = event.attendees.find(
        a => a.user?._id === currentUser._id || a.user === currentUser._id
      );
      if (userAttendee) {
        setRsvpStatus(userAttendee.status || 'no-response');
      } else {
        setRsvpStatus('no-response');
      }
    }
  };
  
  // Handle RSVP submission
  const handleRSVP = async () => {
    if (!selectedEvent || !currentUser) return;
    
    try {
      const response = await fetchWithAuth(`${API_URL}/events/${selectedEvent._id}/rsvp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: rsvpStatus,
          reason: rsvpReason
        })
      });
      
      if (response.success) {
        // Refresh events to show updated RSVP
        fetchEvents();
        setShowRSVPDialog(false);
        setRsvpReason('');
        // Update the selected event with new RSVP
        const updatedEvent = {
          ...selectedEvent,
          attendees: response.data.attendees
        };
        setSelectedEvent(updatedEvent);
      }
    } catch (error) {
      console.error('Error updating RSVP:', error);
      alert('Failed to update RSVP');
    }
  };
  
  // Handle event creation
  const handleEventCreate = (newEvent) => {
    console.log('handleEventCreate called with:', newEvent);
    // Refresh events after creation
    fetchEvents();
    // Close the modal
    setShowCreateModal(false);
  };

  // Handle event deletion
  const handleEventDelete = (event) => {
    setEventToDelete(event);
    setShowDeleteDialog(true);
    setShowEventModal(false);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = async (choice) => {
    if (!eventToDelete) return;
    
    const deleteAll = choice === 'all';
    
    try {
      const url = deleteAll 
        ? `${API_URL}/events/${eventToDelete._id}?deleteAll=true`
        : `${API_URL}/events/${eventToDelete._id}`;
        
      const response = await fetchWithAuth(url, {
        method: 'DELETE'
      });
      
      if (response.success) {
        // Refresh events after deletion
        fetchEvents();
        setShowDeleteDialog(false);
        setEventToDelete(null);
      } else {
        alert('Failed to delete event: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    }
  };

  // Handle event edit
  const handleEventEdit = () => {
    setEventToEdit(selectedEvent);
    setShowEventModal(false);
    setShowEditModal(true);
  };

  // Handle event update
  const handleEventUpdate = async (updatedEvent) => {
    console.log('handleEventUpdate called with:', updatedEvent);
    // Refresh events after update
    fetchEvents();
    // Close the modal
    setShowEditModal(false);
    setEventToEdit(null);
  };
  
  // Copy public RSVP link
  const copyPublicRSVPLink = () => {
    if (!selectedEvent?.publicRsvpToken) return;
    
    const link = `${window.location.origin}/rsvp/${selectedEvent.publicRsvpToken}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    
    // Reset after 2 seconds
    setTimeout(() => setCopiedLink(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading events...</p>
        </div>
      </div>
    );
  }

  const renderEventsList = (eventsToShow) => {
    const now = new Date();
    const futureEvents = eventsToShow.filter(e => new Date(e.startTime) > now && e !== nextEvent);
    const pastEvents = eventsToShow.filter(e => new Date(e.startTime) <= now);
    
    return (
      <>
        {/* Next Event Highlight */}
        {nextEvent && eventsToShow.includes(nextEvent) && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">Next Event</h2>
            <EventCard event={nextEvent} isNext={true} onMenuClick={handleSelectEvent} />
          </div>
        )}
        
        {/* Events List */}
        <div className="space-y-4">
          {/* Upcoming Events */}
          {futureEvents.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 text-muted-foreground">Upcoming Events</h2>
              <div className="space-y-3">
                {futureEvents.map((event) => (
                  <EventCard key={event._id} event={event} onMenuClick={handleSelectEvent} />
                ))}
              </div>
            </div>
          )}
          
          {/* Past Events */}
          {pastEvents.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 text-muted-foreground">Past Events</h2>
              <div className="space-y-3">
                {pastEvents.map((event) => (
                  <EventCard key={event._id} event={event} onMenuClick={handleSelectEvent} />
                ))}
              </div>
            </div>
          )}
          
          {/* Empty state */}
          {eventsToShow.length === 0 && (
            <div className="text-center py-12">
              <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">
                {activeTab === 'my' && 'No personal events found.'}
                {activeTab === 'band' && 'No band events found.'}
                {activeTab === 'all' && 'No events found.'}
              </p>
              {isAuthenticated && (
                <button
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                  onClick={() => {
                    setSelectedDate(new Date());
                    setShowCreateModal(true);
                  }}
                >
                  Create Your First Event
                </button>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  const filteredEvents = getFilteredEvents();
  const nextEvent = getNextEvent();

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Events</h1>
        
        {/* Local action buttons */}
        <div className="flex gap-2 mb-4">
          {isAuthenticated && (
            <button
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
              onClick={() => {
                setSelectedDate(new Date());
                setShowCreateModal(true);
              }}
            >
              <Plus className="w-3 h-3" />
              Create Event
            </button>
          )}
        </div>
        
        {/* Filter controls */}
        <div className="flex flex-wrap gap-2 items-center mb-4">
          <select 
            className="px-3 py-2 border border-border rounded-md bg-background"
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          >
            <option value="">All Event Types</option>
            <option value="rehearsal">Rehearsals</option>
            <option value="concert">Concerts</option>
            <option value="recording">Recording Sessions</option>
            <option value="meeting">Meetings</option>
            <option value="audition">Auditions</option>
          </select>
          
          {bands.length > 0 && (
            <select 
              className="px-3 py-2 border border-border rounded-md bg-background"
              value={filters.band}
              onChange={(e) => setFilters({ ...filters, band: e.target.value })}
            >
              <option value="">All Bands</option>
              {bands.map(band => (
                <option key={band._id} value={band._id}>{band.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>
      
      {/* Tabs for authenticated users */}
      {isAuthenticated ? (
        <Tabs defaultValue={isAuthenticated ? 'my' : 'all'} value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="my">My Events</TabsTrigger>
            <TabsTrigger value="band">Band Events</TabsTrigger>
            <TabsTrigger value="all">All Events</TabsTrigger>
          </TabsList>
          
          <TabsContent value="my">
            {renderEventsList(filteredEvents)}
          </TabsContent>
          
          <TabsContent value="band">
            {renderEventsList(filteredEvents)}
          </TabsContent>
          
          <TabsContent value="all">
            {renderEventsList(filteredEvents)}
          </TabsContent>
        </Tabs>
      ) : (
        renderEventsList(filteredEvents)
      )}
      
      {/* Event detail modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg p-6 max-w-md w-full border border-border">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">{selectedEvent.title}</h2>
              <button 
                className="text-muted-foreground hover:text-foreground p-1"
                onClick={() => setShowEventModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded border ${getEventTypeStyle(selectedEvent.type)}`}>
                  {selectedEvent.type}
                </span>
                {(selectedEvent.recurring || selectedEvent.isRecurringInstance) && (
                  <div className="flex items-center text-primary font-medium">
                    <RefreshCw className="w-4 h-4 mr-1" />
                    <span className="text-sm">
                      {selectedEvent.recurring ? 'Recurring' : 'Series'}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <span className="font-medium">Start:</span> {formatDateTime(selectedEvent.startTime)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <span className="font-medium">End:</span> {formatDateTime(selectedEvent.endTime)}
                  </div>
                </div>
                
                {selectedEvent.band?.name && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <span className="font-medium">Band:</span>{' '}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowEventModal(false);
                          navigate(`/band/${selectedEvent.band._id}`);
                        }}
                        className="text-primary hover:underline"
                      >
                        {selectedEvent.band.name}
                      </button>
                    </div>
                  </div>
                )}
                
                {selectedEvent.location?.name && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div>
                      <span className="font-medium">Location:</span> {selectedEvent.location.name}
                      {selectedEvent.location.address && (
                        <div className="text-muted-foreground text-xs mt-1">
                          {selectedEvent.location.address}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {selectedEvent.description && (
                <div className="pt-2 border-t border-border">
                  <p className="font-medium text-sm mb-1">Description</p>
                  <p className="text-sm text-muted-foreground">{selectedEvent.description}</p>
                </div>
              )}
              
              {selectedEvent.recurring && selectedEvent.recurrenceRule && (
                <div className="pt-2 border-t border-border">
                  <p className="font-medium text-sm mb-1">Recurrence</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedEvent.recurrenceRule.frequency} 
                    {selectedEvent.recurrenceRule.endDate && 
                      ` until ${formatDate(selectedEvent.recurrenceRule.endDate)}`
                    }
                  </p>
                </div>
              )}
              
              {selectedEvent.setlist && (
                <div className="pt-2 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Music className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <span className="font-medium text-sm">Setlist:</span>{' '}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowEventModal(false);
                            navigate(`/setlist/${selectedEvent.setlist._id}`);
                          }}
                          className="text-primary hover:underline text-sm"
                        >
                          {selectedEvent.setlist.name}
                        </button>
                        {selectedEvent.setlist.sheets && (
                          <span className="text-xs text-muted-foreground ml-1">
                            ({selectedEvent.setlist.sheets.length} songs)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Public RSVPs */}
              {selectedEvent.publicRsvps && selectedEvent.publicRsvps.length > 0 && (
                <div className="pt-2 border-t border-border">
                  <p className="font-medium text-sm mb-2">Public RSVPs ({selectedEvent.publicRsvps.length})</p>
                  <div className="space-y-1">
                    {selectedEvent.publicRsvps.slice(0, 5).map((rsvp, idx) => (
                      <div key={idx} className="flex justify-between text-xs">
                        <span>{rsvp.name}</span>
                        <span className={
                          rsvp.status === 'confirmed' ? 'text-green-600' :
                          rsvp.status === 'declined' ? 'text-red-600' :
                          'text-yellow-600'
                        }>
                          {rsvp.status === 'confirmed' ? '✓ Going' :
                           rsvp.status === 'declined' ? '✗ Not Going' :
                           '? Maybe'}
                        </span>
                      </div>
                    ))}
                    {selectedEvent.publicRsvps.length > 5 && (
                      <p className="text-xs text-muted-foreground">
                        ...and {selectedEvent.publicRsvps.length - 5} more
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Share Public RSVP Link */}
            {selectedEvent?.publicRsvpToken && (
              <div className="p-3 bg-muted/50 rounded-md mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Share2 className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Public RSVP Link</span>
                  </div>
                  <button
                    onClick={copyPublicRSVPLink}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
                  >
                    {copiedLink ? (
                      <>
                        <Check className="w-3 h-3" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Share this link with band members who don't have accounts yet
                </p>
              </div>
            )}
            
            <div className="flex justify-between gap-2">
              <div className="flex gap-2">
                {isAuthenticated && (
                  <>
                    {/* Show edit and delete buttons only if user is the owner */}
                    {(selectedEvent.owner?._id === currentUser?._id || 
                      selectedEvent.owner === currentUser?._id) && (
                      <>
                        <button 
                          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                          onClick={handleEventEdit}
                        >
                          Edit
                        </button>
                        <button 
                          className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                          onClick={() => handleEventDelete(selectedEvent)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                    {/* Show current RSVP status and button */}
                    <div className="flex items-center gap-2">
                      {(() => {
                        const userAttendee = selectedEvent.attendees?.find(
                          a => a.user?._id === currentUser?._id || a.user === currentUser?._id
                        );
                        const status = userAttendee?.status || 'no-response';
                        const statusColors = {
                          'confirmed': 'bg-green-100 text-green-800 border-green-200',
                          'declined': 'bg-red-100 text-red-800 border-red-200',
                          'maybe': 'bg-yellow-100 text-yellow-800 border-yellow-200',
                          'no-response': 'bg-gray-100 text-gray-800 border-gray-200'
                        };
                        const statusLabels = {
                          'confirmed': '✓ Going',
                          'declined': '✗ Not Going',
                          'maybe': '? Maybe',
                          'no-response': 'No Response'
                        };
                        
                        return (
                          <>
                            <span className={`text-xs px-2 py-1 rounded border ${statusColors[status]}`}>
                              {statusLabels[status]}
                            </span>
                            <button 
                              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                              onClick={() => setShowRSVPDialog(true)}
                            >
                              RSVP
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>
              <button 
                className="px-4 py-2 bg-muted text-muted-foreground rounded-md hover:bg-muted/80"
                onClick={() => setShowEventModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Event creation modal */}
      <EventModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleEventCreate}
        initialDate={selectedDate}
      />
      
      {/* Event edit modal */}
      <EventModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEventToEdit(null);
        }}
        onSave={handleEventUpdate}
        initialDate={eventToEdit?.startTime}
        eventData={eventToEdit}
        isEditMode={true}
      />
      
      {/* Delete Confirmation Dialog */}
      <DeleteEventDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setEventToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        eventType={
          eventToDelete?.recurring ? 'recurring' : 
          eventToDelete?.isRecurringInstance ? 'recurringInstance' : 
          'single'
        }
        eventTitle={eventToDelete?.title || ''}
      />
      
      {/* RSVP Dialog */}
      {showRSVPDialog && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-background rounded-lg p-6 max-w-md w-full border border-border">
            <h3 className="text-lg font-semibold mb-4">RSVP for {selectedEvent.title}</h3>
            
            <div className="space-y-3 mb-6">
              <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-muted/50">
                <input
                  type="radio"
                  name="rsvpStatus"
                  value="confirmed"
                  checked={rsvpStatus === 'confirmed'}
                  onChange={(e) => setRsvpStatus(e.target.value)}
                  className="mr-3"
                />
                <span className="flex-1">✓ Yes, I'll be there</span>
              </label>
              
              <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-muted/50">
                <input
                  type="radio"
                  name="rsvpStatus"
                  value="declined"
                  checked={rsvpStatus === 'declined'}
                  onChange={(e) => setRsvpStatus(e.target.value)}
                  className="mr-3"
                />
                <span className="flex-1">✗ No, I can't make it</span>
              </label>
              
              <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-muted/50">
                <input
                  type="radio"
                  name="rsvpStatus"
                  value="maybe"
                  checked={rsvpStatus === 'maybe'}
                  onChange={(e) => setRsvpStatus(e.target.value)}
                  className="mr-3"
                />
                <span className="flex-1">? Maybe</span>
              </label>
            </div>
            
            {rsvpStatus === 'declined' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Reason (optional)</label>
                <textarea
                  value={rsvpReason}
                  onChange={(e) => setRsvpReason(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  rows="2"
                  placeholder="Let them know why you can't make it..."
                />
              </div>
            )}
            
            <div className="flex justify-between items-center">
              <div className="text-xs text-muted-foreground">
                {selectedEvent.attendees?.filter(a => a.status === 'confirmed').length || 0} confirmed,
                {' '}{selectedEvent.attendees?.filter(a => a.status === 'declined').length || 0} declined
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowRSVPDialog(false);
                    setRsvpReason('');
                  }}
                  className="px-4 py-2 border rounded-md hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRSVP}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                >
                  Update RSVP
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;